/**
 * Gomme magique : how it works, over the erase screen — a photo where a walker gets circled then
 * vanishes, on a loop, "Gomme magique", one sentence and "C'est parti". A card in the middle on
 * desktop (MagicEraserPopup), a sheet from the bottom on a phone (sheet : true, EditPhoto).
 *
 * Shown on its own the first time the erase screen opens for an account (shouldOpen, stored in
 * User.notifications), and again with the screen's "?". Its viewport is the area it darkens. The
 * sheet also closes when pulled down by its top, like mobile.common.photosimport.PhotoSourceSheet.
 *
 *   let tutorial = new shared.magiceraser.EraserTutorial({ sheet : true, onClose : () => ... });
 *   tutorial.open();   tutorial.close();
 */
fx.ns("shared.magiceraser", class EraserTutorial extends fx.Component {

    constructor({ sheet = false, onClose = () => {} } = {})
    {
        super();

        this.sheet = sheet;
        this.onClose = onClose;
        this.visibleAtStart = false;

        this._onPullStart = this._onPullStart.bind(this);
        this._onPullMove = this._onPullMove.bind(this);
        this._onPullEnd = this._onPullEnd.bind(this);
    }

    // px the sheet is pulled down before it goes, and the time it takes to slide away
    get PULL_TO_CLOSE() { return 60; }
    get SLIDE_OUT() { return 250; }

    static get NOTIFICATION()
    {
        return "magic-eraser-tutorial";
    }

    // true the first time only for this account, whatever the device or the editor
    // (User.notifications) : the screen then opens the tutorial, which counts as seen from then on
    static shouldOpen(account)
    {
        if (!account || account.wasNotificatedForAccount(this.NOTIFICATION))
            return false;

        account.notificatedForAccount(this.NOTIFICATION);

        return true;
    }

    // the outline round the walker, in % of the picture (viewBox 0 0 100 100)
    get PATH()
    {
        return "M91 46 C88 46 87 42 87 38 C86 33 85.5 28 86 24 C86.5 21 89 20 91 20.5 C94 21 96 23 96 28 C96.5 34 96 40 95 43 C94 45.5 93 46 91 46 Z";
    }

    // the card slides in : down from the top on desktop, up from the bottom as a sheet
    open()
    {
        this.visible = true;
        this.removeClass("opening");
        void this.node.offsetWidth;   // restart the animation when « ? » opens it again
        this.addClass("opening");
        this.delay(this._placeCursor, 0);
    }

    // it slides away first : the card back up on desktop, the sheet down from wherever a pull left it
    close()
    {
        if (!this.visible)
        {
            this._hide();
            return;
        }

        if (this._closing)
            return;

        this._closing = true;
        this.removeClass("pulling");
        this.addClass("closing");
        this.removeClass("opening");
        this.find(".card").style.transform = this.sheet ? "translateY(100%)" : "translateY(-100vh)";
        this.delay(this._hide, this.SLIDE_OUT);
    }

    _hide()
    {
        this._closing = false;
        this.removeClass("closing");
        this.removeClass("pulling");

        let card = this.find(".card");
        if (card)
            card.style.transform = "";

        this.visible = false;
        this.onClose();
    }

    get isOpen()
    {
        return this.visible;
    }

    onReady()
    {
        super.onReady();

        this.toggleClass("sheet", this.sheet);
        this.on(this.sheet ? "tap" : "click", this.close, this.find(".go"));

        // done sliding in : the opening animation would otherwise hold the card's transform, and a
        // pull could not move it
        this.on("animationend", (e) => { if (e.target == this.find(".card")) this.removeClass("opening"); }, this.find(".card"));

        let grab = this.find(".grab");
        if (grab)
        {
            grab.addEventListener("touchstart", this._onPullStart, { passive : true });
            grab.addEventListener("touchmove", this._onPullMove, { passive : false });
            grab.addEventListener("touchend", this._onPullEnd);
            grab.addEventListener("touchcancel", this._onPullEnd);
        }
    }

    destroy()
    {
        let grab = this.find(".grab");
        if (grab)
        {
            grab.removeEventListener("touchstart", this._onPullStart);
            grab.removeEventListener("touchmove", this._onPullMove);
            grab.removeEventListener("touchend", this._onPullEnd);
            grab.removeEventListener("touchcancel", this._onPullEnd);
        }

        super.destroy();
    }

    // pulling the top of the sheet down : it follows the finger, and far enough closes it
    _onPullStart(e)
    {
        if (this._closing || !e.touches.length)
            return;

        this._pullFrom = e.touches[0].clientY;
        this._pulled = 0;
        this.removeClass("opening");
        this.addClass("pulling");   // no easing while it follows the finger
    }

    _onPullMove(e)
    {
        if (this._pullFrom == null || !e.touches.length)
            return;

        e.preventDefault();
        this._pulled = Math.max(0, e.touches[0].clientY - this._pullFrom);
        this.find(".card").style.transform = "translateY(" + this._pulled + "px)";
    }

    _onPullEnd()
    {
        if (this._pullFrom == null)
            return;

        let pulled = this._pulled;

        this._pullFrom = null;
        this._pulled = 0;
        this.removeClass("pulling");   // the snap back animates

        if (pulled > this.PULL_TO_CLOSE)
            this.close();
        else
            this.find(".card").style.transform = "";
    }

    onDisplayUpdated()
    {
        super.onDisplayUpdated();

        this._placeCursor();
    }

    render()
    {
        let base = "/app/assets/images/magiceraser/";

        return  '<div class="card">' +
                    (this.sheet ? '<div class="grabber"></div><div class="grab"></div>' : '') +
                    '<div class="demo">' +
                        '<img class="before" src="' + base + 'tutorial-before.jpg" alt="" draggable="false">' +
                        '<img class="after" src="' + base + 'tutorial-after.jpg" alt="" draggable="false">' +
                        '<svg viewBox="0 0 100 100" preserveAspectRatio="none">' +
                            '<path class="fill" d="' + this.PATH + '"></path>' +
                            '<path class="outline" d="' + this.PATH + '" pathLength="1"></path>' +
                        '</svg>' +
                        '<div class="cursor"></div>' +
                    '</div>' +
                    '<div class="texts">' +
                        '<div class="title">' + fx.text("Gomme magique") + '</div>' +
                        '<div class="text">' + fx.text("Entourez un élément pour le supprimer de votre photo.") + '</div>' +
                    '</div>' +
                    '<div class="go">' + fx.text("C'est parti") + '</div>' +
                '</div>';
    }

    // the cursor follows the outline through offset-path, which takes pixels : the path scaled to
    // the picture's size on show
    _placeCursor()
    {
        let demo = this.find(".demo");
        let cursor = this.find(".cursor");
        if (!demo || !cursor || !demo.offsetWidth)
            return;

        let kx = demo.offsetWidth / 100;
        let ky = demo.offsetHeight / 100;
        let i = 0;
        let path = this.PATH.replace(/[\d.]+/g, (n) => (Number(n) * (i++ % 2 ? ky : kx)).toFixed(1));

        cursor.style.offsetPath = "path('" + path + "')";
    }
});
